import { useContext, useEffect, useState } from 'react';
import { ChatContext, ChatContextInterface } from './MSRContext';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdSignalWifiOff } from 'react-icons/md';

export default function ConnectionStatusBanner() {
  const context = useContext(ChatContext) as ChatContextInterface;
  const [show, setShow] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  useEffect(() => {
    // Show banner when not connected or reconnecting
    const shouldShow =
      context.connectionState === 'reconnecting' ||
      context.connectionState === 'connecting' ||
      context.connectionState === 'offline' ||
      context.connectionState === 'disconnected';

    setShow(shouldShow);

    // Update queue size periodically when offline/reconnecting
    if (shouldShow && context.wrapper) {
      const checkQueue = () => {
        const size = (context.wrapper as any)?.getQueueSize?.() || 0;
        setQueueSize(size);
      };

      // Check immediately
      checkQueue();

      // Check every 500ms while banner is shown
      const interval = setInterval(checkQueue, 500);
      return () => clearInterval(interval);
    }
  }, [context.connectionState, context.wrapper]);

  if (!show) return null;

  const getStatusConfig = () => {
    const queueText = queueSize > 0 ? ` • ${queueSize} message${queueSize > 1 ? 's' : ''} queued` : '';

    switch (context.connectionState) {
      case 'reconnecting':
        return {
          icon: <AiOutlineLoading3Quarters className="h-5 w-5 animate-spin" />,
          text: `Reconnecting...${queueText}`,
          bgColor: 'bg-yellow-500',
        };
      case 'connecting':
        return {
          icon: <AiOutlineLoading3Quarters className="h-5 w-5 animate-spin" />,
          text: 'Connecting...',
          bgColor: 'bg-blue-500',
        };
      case 'offline':
        return {
          icon: <MdSignalWifiOff className="h-5 w-5" />,
          text: `No internet connection${queueText ? ' • Messages will send when connected' : ''}`,
          bgColor: 'bg-red-600',
        };
      case 'disconnected':
        return {
          icon: <MdSignalWifiOff className="h-5 w-5" />,
          text: `Connection lost${queueText ? ' • Messages will send when connected' : ''}`,
          bgColor: 'bg-red-500',
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 ${config.bgColor} text-white py-2 px-4 shadow-md z-50`}>
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
        {config.icon}
        <span className="text-sm font-medium">{config.text}</span>
      </div>
    </div>
  );
}
